import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { FileCheck2, FileWarning, ScrollText, ArrowRight } from 'lucide-react';
import { AppLayout } from '@/components/Layout/AppLayout';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { EmptyState } from '@/components/common/EmptyState';
import { getSourceOverview, type SourceOverview, type SourceWorklistEntry } from '@/api/sources.api';

// ─── Belege-Cockpit ──────────────────────────────────────────────────────────
//
// Tree-weiter Überblick über die Beleglage: Wie gut ist der Baum durch Quellen
// gedeckt, und welche gut dokumentierten Personen haben noch keinen Beleg? Die
// Arbeitsliste verlinkt direkt in den Stammbaum (Person auswählen), wo im
// „Belege"-Tab eine Quelle nachgetragen werden kann.

function useSourceOverview() {
  return useQuery<SourceOverview>({
    queryKey: ['source-overview'],
    queryFn: getSourceOverview,
  });
}

// ─── Hilfskomponenten ─────────────────────────────────────────────────────────

function StatCard({
  icon,
  value,
  label,
  tone = 'slate',
}: {
  icon: React.ReactNode;
  value: string | number;
  label: string;
  tone?: 'slate' | 'green' | 'amber';
}) {
  const toneClasses: Record<string, string> = {
    slate: 'text-slate-700',
    green: 'text-emerald-600',
    amber: 'text-amber-600',
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2 text-slate-400">{icon}</div>
      <div className={`mt-2 text-2xl font-bold ${toneClasses[tone]}`}>{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}

function CoverageBar({ coverage }: { coverage: number }) {
  const pct = Math.round(coverage * 100);
  // Farbe nach Abdeckung: rot < 34 %, amber < 67 %, sonst grün.
  const color = pct >= 67 ? 'bg-emerald-500' : pct >= 34 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between">
        <span className="text-sm font-medium text-slate-600">Belegquote</span>
        <span className="text-sm font-semibold text-slate-800">{pct}%</span>
      </div>
      <div className="h-3 w-full overflow-hidden rounded-full bg-slate-100">
        <div className={`h-3 rounded-full ${color} transition-all`} style={{ width: `${Math.max(pct, 1)}%` }} />
      </div>
    </div>
  );
}

function personName(p: SourceWorklistEntry): string {
  const full = [p.firstName, p.lastName].filter(Boolean).join(' ');
  return full || 'Unbenannt';
}

function lifespan(p: SourceWorklistEntry): string {
  const b = p.birthDateText?.trim();
  const d = p.deathDateText?.trim();
  if (b && d) return `${b} – ${d}`;
  if (b) return `* ${b}`;
  if (d) return `† ${d}`;
  return '';
}

// ─── Seite ────────────────────────────────────────────────────────────────────

export function SourceCoveragePage() {
  const { data, isLoading, isError, refetch } = useSourceOverview();
  const navigate = useNavigate();

  const maxFacts = useMemo(
    () => data?.worklist.reduce((m, p) => Math.max(m, p.factCount), 0) ?? 0,
    [data],
  );

  return (
    <AppLayout>
      <div className="h-full overflow-auto p-4 sm:p-6">
        <div className="mx-auto max-w-4xl">
          <header className="mb-6">
            <h1 className="text-2xl font-bold text-slate-800">Belege</h1>
            <p className="mt-1 text-sm text-slate-500">
              Wie gut ist der Stammbaum durch Quellen gedeckt – und wo fehlt noch ein Beleg?
            </p>
          </header>

          {isLoading && (
            <div className="flex justify-center py-20">
              <LoadingSpinner size="lg" message="Beleglage wird ausgewertet..." />
            </div>
          )}

          {isError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-center">
              <p className="text-sm text-red-700">Die Beleglage konnte nicht geladen werden.</p>
              <button
                onClick={() => refetch()}
                className="mt-2 text-sm font-medium text-red-600 underline hover:text-red-800"
              >
                Erneut versuchen
              </button>
            </div>
          )}

          {data && (
            <div className="space-y-6">
              {/* Kennzahlen */}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <StatCard
                  icon={<FileCheck2 className="h-4 w-4" />}
                  value={data.totals.personsSourced}
                  label="belegte Personen"
                  tone="green"
                />
                <StatCard
                  icon={<FileWarning className="h-4 w-4" />}
                  value={data.totals.personsUnsourcedWithData}
                  label="unbelegt (mit Daten)"
                  tone="amber"
                />
                <StatCard
                  icon={<ScrollText className="h-4 w-4" />}
                  value={data.totals.sources}
                  label="Quellen gesamt"
                />
                <StatCard
                  icon={<FileCheck2 className="h-4 w-4" />}
                  value={data.totals.personsWithData}
                  label="dokumentierte Personen"
                />
              </div>

              {/* Abdeckungsbalken */}
              <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <CoverageBar coverage={data.totals.coverage} />
                <p className="mt-2 text-xs text-slate-500">
                  {data.totals.personsSourced} von {data.totals.personsWithData} dokumentierten
                  Personen sind durch mindestens eine Quelle belegt. (Reine Namens-Einträge ohne
                  weitere Daten bleiben unberücksichtigt.)
                </p>
              </div>

              {/* Arbeitsliste */}
              <div>
                <h2 className="mb-1 text-lg font-semibold text-slate-800">
                  Zu belegen{' '}
                  <span className="text-sm font-normal text-slate-400">
                    ({data.totals.personsUnsourcedWithData})
                  </span>
                </h2>
                <p className="mb-3 text-sm text-slate-500">
                  Dokumentierte Personen ohne Quelle – die am besten dokumentierten zuerst. Klick
                  öffnet die Person im Stammbaum.
                </p>

                {data.worklist.length === 0 ? (
                  <EmptyState
                    title="Alles belegt"
                    description="Jede dokumentierte Person hat mindestens eine Quelle. Saubere Arbeit!"
                  />
                ) : (
                  <ul className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                    {data.worklist.map((p) => {
                      const span = lifespan(p);
                      return (
                        <li key={p.id}>
                          <button
                            onClick={() => navigate(`/?person=${p.id}`)}
                            className="group flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50"
                          >
                            {/* Dringlichkeit: kleine Balken nach Faktenzahl */}
                            <span
                              className="hidden h-8 w-1.5 shrink-0 rounded-full bg-amber-400 sm:block"
                              style={{ opacity: maxFacts > 0 ? 0.35 + 0.65 * (p.factCount / maxFacts) : 1 }}
                              aria-hidden="true"
                            />
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-sm font-medium text-slate-800">
                                {personName(p)}
                                {p.birthName && p.birthName !== p.lastName && (
                                  <span className="font-normal text-slate-400"> geb. {p.birthName}</span>
                                )}
                              </div>
                              {span && <div className="truncate text-xs text-slate-500">{span}</div>}
                            </div>
                            <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                              {p.factCount} {p.factCount === 1 ? 'Fakt' : 'Fakten'}
                            </span>
                            <ArrowRight className="h-4 w-4 shrink-0 text-slate-300 transition-colors group-hover:text-slate-500" aria-hidden="true" />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}

                {data.worklistTruncated && (
                  <p className="mt-3 text-center text-xs text-slate-400">
                    Nur die dringlichsten Einträge werden angezeigt. Nach dem Belegen aktualisiert
                    sich die Liste.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
